import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CaretRight, GearSix, X } from '@phosphor-icons/react'
import clsx from 'clsx'
import { useUiStore } from '@/stores/uiStore'
import { categoryService, tagService } from '@/services/api'
import { type Category } from '@/types/category'

function SidebarContent() {
	const location = useLocation()
	const setSidebarOpen = useUiStore((s) => s.setSidebarOpen)

	const prevPathname = useRef(location.pathname)
	useEffect(() => {
		if (prevPathname.current === location.pathname) return
		prevPathname.current = location.pathname
		const mq = window.matchMedia('(max-width: 767px)')
		if (mq.matches) {
			setSidebarOpen(false)
		}
	}, [location.pathname, setSidebarOpen])

	return (
		<div className="flex h-full flex-col">
			<div className="flex-1 overflow-y-auto p-4">
				{/* Category tree */}
				<CategoryTree />

				{/* Divider */}
				<div className="my-4 border-t border-stone-200" />

				{/* Tag cloud */}
				<TagCloud />
			</div>

			{/* Bottom link to category management */}
			<div className="border-t border-stone-200 p-3">
				<Link
					to="/categories"
					className={clsx(
						'flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium no-underline transition-colors',
						location.pathname === '/categories'
							? 'bg-primary-50 text-primary-700'
							: 'text-stone-500 hover:bg-stone-50 hover:text-stone-700',
					)}
				>
					<GearSix size={14} />
					Управление категориями
				</Link>
			</div>
		</div>
	)
}

/* ────────────── Category Tree ────────────── */

function CategoryTree() {
	const { data: categories = [] } = useQuery({
		queryKey: ['categories'],
		queryFn: categoryService.list,
		staleTime: 10 * 60 * 1000,
	})

	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

	useEffect(() => {
		if (categories.length > 0) {
			setExpandedIds((prev) => {
				if (prev.size > 0) return prev
				return new Set(categories.filter((c) => c.children && c.children.length > 0).map((c) => c.id))
			})
		}
	}, [categories])

	const toggleExpanded = useCallback((id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) {
				next.delete(id)
			} else {
				next.add(id)
			}
			return next
		})
	}, [])

	if (categories.length === 0) {
		return (
			<div>
				<h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
					Категории
				</h3>
				<p className="px-1 text-xs text-stone-400">Нет категорий</p>
			</div>
		)
	}

	return (
		<div>
			<h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
				Категории
			</h3>
			<ul className="space-y-0.5">
				{categories.map((cat) => (
					<SidebarCategoryNode
						key={cat.id}
						category={cat}
						expandedIds={expandedIds}
						onToggleExpanded={toggleExpanded}
					/>
				))}
			</ul>
		</div>
	)
}

function SidebarCategoryNode({
	category,
	expandedIds,
	onToggleExpanded,
}: {
	category: Category
	expandedIds: Set<string>
	onToggleExpanded: (id: string) => void
}) {
	const location = useLocation()
	const hasChildren = category.children && category.children.length > 0
	const isExpanded = expandedIds.has(category.id)
	const isActive = location.pathname === `/categories/${category.id}`

	return (
		<li>
			<div className="flex items-center">
				{hasChildren ? (
					<button
						type="button"
						onClick={() => onToggleExpanded(category.id)}
						className={clsx(
							'shrink-0 rounded p-0.5 text-stone-400 transition-transform hover:text-stone-600',
							isExpanded && 'rotate-90',
						)}
					>
						<CaretRight size={12} weight="bold" />
					</button>
				) : (
					<span className="w-4" />
				)}
				<Link
					to={`/categories/${category.id}`}
					className={clsx(
						'min-w-0 flex-1 truncate rounded-md px-2 py-1 text-sm no-underline transition-colors',
						isActive
							? 'font-medium text-primary-700'
							: 'text-stone-700 hover:text-primary-600',
					)}
				>
					{category.name}
				</Link>
			</div>
			{hasChildren && isExpanded && (
				<ul className="ml-4 space-y-0.5">
					{category.children!.map((child) => (
						<SidebarCategoryNode
							key={child.id}
							category={child}
							expandedIds={expandedIds}
							onToggleExpanded={onToggleExpanded}
						/>
					))}
				</ul>
			)}
		</li>
	)
}

/* ────────────── Tag Cloud ────────────── */

function TagCloud() {
	const location = useLocation()

	const { data: tags = [] } = useQuery({
		queryKey: ['tags', ''],
		queryFn: () => tagService.search(''),
		staleTime: 10 * 60 * 1000,
	})

	if (tags?.length === 0) return null

	return (
		<div>
			<h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
				Теги
			</h3>
			<div className="flex flex-wrap gap-1.5">
				{tags?.map((tag) => {
					const isActive = location.pathname === `/tags/${tag.id}`
					return (
						<Link
							key={tag.id}
							to={`/tags/${tag.id}`}
							className={clsx(
								'rounded-full px-2.5 py-0.5 text-xs font-medium no-underline transition-colors',
								isActive
									? 'bg-primary-600 text-white'
									: 'bg-stone-100 text-stone-600 hover:bg-primary-50 hover:text-primary-700',
							)}
						>
							{tag.name}
						</Link>
					)
				})}
			</div>
		</div>
	)
}

/* ────────────── Sidebar Shell ────────────── */

export function Sidebar() {
	const setSidebarOpen = useUiStore((s) => s.setSidebarOpen)

	return (
		<>
			{/* Mobile overlay */}
			<div
				className="fixed inset-0 z-30 bg-black/30 md:hidden"
				onClick={() => setSidebarOpen(false)}
			/>
			<aside
				className={clsx(
					'shrink-0 border-r border-stone-200 bg-surface-elevated',
					'fixed top-0 bottom-0 left-0 z-40 w-64',
					'md:static md:top-auto md:bottom-auto md:z-auto',
				)}
			>
				<div className="absolute top-0 right-0  p-2 md:hidden">
					<button
						onClick={() => setSidebarOpen(false)}
						className="rounded-md p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-700"
					>
						<X size={20} />
					</button>
				</div>
				<SidebarContent />
			</aside>
		</>
	)
}
